import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PERSON_STATUSES, type PersonDetailWire } from "@releve/shared";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  StandardDialog,
  StandardDialogBody,
  StandardDialogContent,
  StandardDialogDescription,
  StandardDialogFooter,
  StandardDialogHeader,
  StandardDialogTitle,
} from "@/components/ui/standard-dialog";
import { useCreatePerson } from "@/hooks/usePeople";
import { useTranslation } from "@/hooks/useTranslation";
import { PersonTypeToggleGroup } from "./person-form/PersonTypeToggleGroup";

const createSchema = (t: (key: string) => string) =>
  z.object({
    first_name: z.string().trim().min(1, t("form.validation.firstNameRequired")),
    last_name: z.string().trim().min(1, t("form.validation.lastNameRequired")),
    middle_name: z.string(),
    preferred_name: z.string(),
    person_type: z.array(z.string()),
    status: z.string().min(1, t("form.validation.statusRequired")),
  });

type QuickCreatePersonValues = z.infer<ReturnType<typeof createSchema>>;

interface QuickCreatePersonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The created person — the caller selects it (e.g. as the other side of a connection). */
  onCreated: (person: PersonDetailWire) => void;
  /** Text the user already typed in the picker's search: first word → first
   *  name, the rest → last name. */
  initialName?: string;
}

function splitName(name: string | undefined): { first: string; last: string } {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] ?? "", last: parts.slice(1).join(" ") };
}

/**
 * A small "just the name" person create, opened from `PersonConnectionDialog`
 * so a connection can point at someone not yet in the directory. Mount it
 * only while open (`{open && <QuickCreatePersonDialog … />}`).
 *
 * The body carries no `organization_id` — the hook posts to the selected
 * organization's route. The submit handler stops propagation: this dialog is
 * portalled, but React submit events still bubble through the component tree
 * to any outer form (the person form hosts the connection dialog's parent).
 */
export function QuickCreatePersonDialog({ open, onOpenChange, onCreated, initialName }: QuickCreatePersonDialogProps) {
  const { t } = useTranslation("people");
  const createPerson = useCreatePerson();
  const schema = useMemo(() => createSchema(t), [t]);

  const initial = splitName(initialName);
  const form = useForm<QuickCreatePersonValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: initial.first,
      last_name: initial.last,
      middle_name: "",
      preferred_name: "",
      person_type: [],
      status: "active",
    },
  });

  const onSubmit = async (values: QuickCreatePersonValues) => {
    try {
      const person = await createPerson.mutateAsync({
        first_name: values.first_name,
        last_name: values.last_name,
        middle_name: values.middle_name || undefined,
        preferred_name: values.preferred_name || undefined,
        person_type: values.person_type,
        status: values.status,
      });
      onCreated(person);
      onOpenChange(false);
    } catch {
      // useCreatePerson toasts the failure and the app's MutationCache
      // reports it; the dialog stays open so nothing typed is lost.
    }
  };

  const nameField = (name: "first_name" | "last_name" | "middle_name" | "preferred_name", labelKey: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel>{t(labelKey)}</FormLabel>
            {fieldState.error && <span className="text-xs text-destructive">{fieldState.error.message}</span>}
          </div>
          <FormControl>
            <Input {...field} />
          </FormControl>
        </FormItem>
      )}
    />
  );

  return (
    <StandardDialog open={open} onOpenChange={onOpenChange}>
      <StandardDialogContent size="lg">
        <StandardDialogHeader>
          <StandardDialogTitle>{t("form.quickCreate.title")}</StandardDialogTitle>
          <StandardDialogDescription>{t("form.quickCreate.description")}</StandardDialogDescription>
        </StandardDialogHeader>
        <Form {...form}>
          <form
            className="contents"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit(onSubmit)(e);
            }}
          >
            <StandardDialogBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {nameField("first_name", "form.fields.firstName")}
                {nameField("last_name", "form.fields.lastName")}
                {nameField("middle_name", "form.fields.middleName")}
                {nameField("preferred_name", "form.fields.preferredName")}
              </div>
              <FormField
                control={form.control}
                name="person_type"
                render={({ field }) => (
                  <div className="space-y-2">
                    <Label>{t("form.fields.personTypes")}</Label>
                    <PersonTypeToggleGroup
                      value={field.value}
                      onChange={field.onChange}
                      ariaLabel={t("form.fields.personTypes")}
                    />
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>{t("form.fields.status")}</FormLabel>
                      {fieldState.error && (
                        <span className="text-xs text-destructive">{fieldState.error.message}</span>
                      )}
                    </div>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("form.placeholders.status")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PERSON_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {t(`common:people.status.${status}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </StandardDialogBody>
            <StandardDialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common:cancel")}
              </Button>
              <Button type="submit" disabled={createPerson.isPending}>
                {createPerson.isPending ? t("form.quickCreate.creating") : t("form.quickCreate.submit")}
              </Button>
            </StandardDialogFooter>
          </form>
        </Form>
      </StandardDialogContent>
    </StandardDialog>
  );
}
